<template>
  <div class="bg-gray-50">
    <div class="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 py-12 lg:grid-cols-[240px_1fr] lg:px-8">
      <aside class="self-start rounded-3xl bg-white p-4 shadow-card ring-1 ring-gray-100 lg:sticky lg:top-20">
        <p class="text-xs font-semibold uppercase tracking-wide text-muted">Terms</p>
        <div class="mt-3 space-y-2 text-sm font-semibold text-ink">
          <button
            v-for="section in sections"
            :key="section.id"
            class="flex w-full items-center justify-between rounded-2xl px-3 py-2 transition"
            :class="activeSection === section.id ? 'bg-brand/10 text-brand' : 'hover:bg-gray-50'"
            @click="scrollToSection(section.id)"
          >
            <span>{{ section.title }}</span>
            <span v-if="activeSection === section.id" class="text-xs text-brand">•</span>
          </button>
        </div>
      </aside>

      <main class="rounded-3xl bg-white p-6 shadow-card ring-1 ring-gray-100">
        <div class="space-y-2 border-b border-gray-100 pb-4">
          <p class="text-xs font-semibold uppercase tracking-wide text-muted">Updated July 2024</p>
          <h1 class="text-3xl font-semibold text-ink">Invi8 Terms of Service</h1>
          <p class="text-sm text-muted">These terms govern how you use Invi8 to create invites, manage events, and work with vendors.</p>
        </div>

        <div class="mt-6 space-y-12">
          <section
            v-for="section in sections"
            :id="section.id"
            :key="section.id"
            class="space-y-3 scroll-mt-20"
            :ref="el => registerSection(el, section.id)"
          >
            <h2 class="text-xl font-semibold text-ink">{{ section.title }}</h2>
            <p v-for="(para, idx) in section.content" :key="idx" class="text-sm text-muted">
              {{ para }}
            </p>
            <ul v-if="section.list" class="list-disc space-y-2 pl-5 text-sm text-muted">
              <li v-for="(item, idx) in section.list" :key="idx">{{ item }}</li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'

const sections = [
  {
    id: 'eligibility',
    title: 'Eligibility & accounts',
    content: [
      'You must be 18+ to use Invi8. Keep your account credentials secure and notify us of any unauthorized use.',
      'Vendors must ensure their listings, pricing, and availability are accurate and lawful.'
    ],
    list: ['Provide accurate info', 'Do not share credentials', 'Keep listings current']
  },
  {
    id: 'usage',
    title: 'Acceptable use',
    content: [
      'Do not misuse the platform: no spam, harassment, unauthorized scraping, or security testing without consent.',
      'We may suspend or remove accounts for policy violations or fraudulent activity.'
    ]
  },
  {
    id: 'payments',
    title: 'Payments & holds',
    content: [
      'Payment processing may be handled by third parties; their terms apply. Refunds or holds follow the vendor’s policy and local laws.',
      'You authorize us to facilitate payments between hosts and vendors for confirmed bookings.'
    ]
  },
  {
    id: 'content',
    title: 'Content & IP',
    content: [
      'You retain rights to your content; you grant Invi8 a license to host and display it for service operation.',
      'Do not upload content you do not have rights to, or that violates laws or third-party rights.'
    ]
  },
  {
    id: 'liability',
    title: 'Liability',
    content: [
      'Invi8 provides tools and vendor listings; we are not a party to contracts between hosts and vendors.',
      'To the extent permitted by law, our liability is limited to the amount you paid us in the last 12 months.'
    ]
  },
  {
    id: 'termination',
    title: 'Termination',
    content: [
      'You may close your account at any time. We may suspend or terminate access for violations of these terms or applicable law.'
    ]
  },
  {
    id: 'contact',
    title: 'Contact',
    content: [
      'Questions about these terms? Reach us at legal@invi8.com or via the contact page.'
    ]
  }
]

const activeSection = ref(sections[0].id)
const sectionRefs = reactive({})
let observer = null
const route = useRoute()

const registerSection = (el, id) => {
  if (el) sectionRefs[id] = el
}

const scrollToSection = (id) => {
  const el = sectionRefs[id]
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  activeSection.value = id
}

const handleIntersect = (entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      activeSection.value = entry.target.id
    }
  })
}

const syncHash = () => {
  const hash = route.hash?.replace('#', '')
  if (hash && sectionRefs[hash]) {
    scrollToSection(hash)
  }
}

onMounted(() => {
  observer = new IntersectionObserver(handleIntersect, {
    root: null,
    rootMargin: '-20% 0px -60% 0px',
    threshold: [0.1, 0.25, 0.5]
  })
  Object.values(sectionRefs).forEach((el) => observer.observe(el))
  window.addEventListener('hashchange', syncHash)
  syncHash()
})

onUnmounted(() => {
  if (observer) {
    Object.values(sectionRefs).forEach((el) => observer.unobserve(el))
    observer.disconnect()
  }
  window.removeEventListener('hashchange', syncHash)
})
</script>
